import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { useToast } from "@smart/bridge-intake-components-dom";
import { buildMessageTemplate } from "@smart/bridge-templates-basic";
import {
  SubmissionLinkHashNS,
  UserNS,
  globalTeamUri,
  type AnalyticsTool,
} from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  ContextMenu,
  type ContextMenuItemProps,
  type DropdownPartProps,
} from "@smart/itops-sb-design-system-dom";
import { extractId } from "@smart/itops-types-basic";
import { toSubdomain } from "@smart/itops-utils-basic";
import {
  useDeleteSubmissionLinkHash,
  useToggleSlug,
  useUpdateFormOwnership,
  useUpdateSubmissionLinkHash,
} from "@smart/manage-gql-client-dom";
import { useUser } from "@smart/manage-hooks-dom";

import { copySupportInformation, GenerateQRCode } from "../../shared";
import {
  DeactivateFormModal,
  DeleteWebEnquiryLinkModal,
  WebEnquiryLinkModal,
  type WebEnquiryLinkFormValues,
} from "../shared";
import { type EmbedType, GqlForm, GqlSubmissionLinkHash } from "../types";

export type FormMenuProps = {
  form: GqlForm;
  hasSubmissionLink: boolean;
  submissionLinkHashes?: GqlSubmissionLinkHash[];
  setDeletingForm: (form: GqlForm) => void;
  setDuplicatingForm: (form: GqlForm) => void;
  setPrintingForm: (form: GqlForm) => void;
};

export const FormMenu = ({
  form,
  hasSubmissionLink,
  submissionLinkHashes,
  setDeletingForm,
  setDuplicatingForm,
  setPrintingForm,
}: FormMenuProps) => {
  const { permission, user } = useUser();
  const { globalForm } = useFeatureFlags();
  const { setToasts } = useToast();
  const [updateSlug, toggleSlug] = useToggleSlug();
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [editingLinkHash, setEditingLinkHash] = useState<
    GqlSubmissionLinkHash | undefined
  >();
  const [deletingLinkHash, setDeletingLinkHash] = useState<
    GqlSubmissionLinkHash | undefined
  >();
  const [updateFormOwnership] = useUpdateFormOwnership();
  const [updateSubmissionLinkHash, { loading: savingLink }] =
    useUpdateSubmissionLinkHash();
  const [deleteSubmissionLinkHash, { loading: deletingLink }] =
    useDeleteSubmissionLinkHash();
  const [generatingQRCode, setGeneratingQRCode] = useState<string>();
  const navigate = useNavigate();
  const { terms } = loadLocale();

  const isGenerating =
    form.aiUserFeedback?.status === "generating" ||
    form.aiUserFeedback?.status === "generated";
  const canActivate =
    !isGenerating &&
    permission?.can("update", {
      type: "slug",
      value: {
        uri: form.rawForm.slug?.uri || "",
        active: form.active ?? false,
        formUri: form.uri,
      },
      parentForm: {
        uri: form.uri,
        teamUri: form.teamUri,
        active: form.active,
      },
    });
  const canView = permission?.can("view", {
    type: "form",
    value: { uri: form.uri, teamUri: form.teamUri, active: form.active },
  });
  const canDuplicate = canView && !isGenerating;
  const canDelete = permission?.can("delete", {
    type: "form",
    value: { uri: form.uri, teamUri: form.teamUri, active: form.active },
  });
  const canMoveToGlobal =
    form.teamUri !== globalTeamUri &&
    permission?.can("add", {
      type: "form",
      value: { uri: form.uri, teamUri: globalTeamUri },
    });

  const setActive = (active: boolean) =>
    updateSlug({
      variables: {
        formUri: form.uri,
        fields: { active },
      },
    });

  const closeLinkModal = () => setEditingLinkHash(undefined);

  const closeDeleteLinkModal = () => setDeletingLinkHash(undefined);

  const confirmDeleteLink = async () => {
    if (!deletingLinkHash) return;
    const { uri } = deletingLinkHash;
    try {
      await deleteSubmissionLinkHash({ variables: { uri } });
      closeDeleteLinkModal();
      setToasts([
        { text: "Link deleted", palette: "default", width: "medium" },
      ]);
    } catch (error) {
      console.error(error);
      setToasts([
        {
          text: `Failed to delete web ${terms.enquiry.name} link`,
          palette: "destructive",
          width: "medium",
        },
      ]);
      closeDeleteLinkModal();
    }
  };

  const onSubmitWebEnquiryLink = async ({
    uri,
    values,
  }: {
    uri?: string;
    values: WebEnquiryLinkFormValues;
  }) => {
    const analyticsTools: AnalyticsTool[] | undefined =
      values.gaMeasurementId.trim().length > 0
        ? [
            {
              type: "googleAnalytics",
              trackingId: values.gaMeasurementId.trim(),
            },
          ]
        : undefined;

    try {
      await updateSubmissionLinkHash({
        variables: {
          uri: uri ?? SubmissionLinkHashNS.generateUri(),
          fields: {
            formUri: form.uri,
            slugUri: form.rawForm.slug?.uri ?? "",
            label: values.linkLabel.trim(),
            notifiedUserUris: values.staffIds.map((id) =>
              UserNS.generateUri("sb", id),
            ),
            analyticsTools,
          },
        },
      });
      setToasts([
        {
          text: uri ? "Link and QR code updated" : "Link and QR code created",
          width: "medium",
        },
      ]);
      closeLinkModal();
    } catch (error) {
      console.error(error);
      setToasts([
        {
          text: uri
            ? "Failed to update link and QR code"
            : "Failed to create link and QR code",
          palette: "destructive",
          width: "medium",
        },
      ]);
    }
  };

  const onCopyLink = (linkHash: GqlSubmissionLinkHash, type: EmbedType) => {
    const link = toSubdomain(undefined, `/create/${extractId(linkHash.uri)}`);

    const code =
      type === "url"
        ? link
        : buildMessageTemplate({
            link,
            format: "embed",
            category: "lead",
            matterType: { name: "" },
            form: { title: "" },
            embedOption: type,
          }).html;

    navigator.clipboard
      .writeText(code)
      .then(() =>
        setToasts([
          {
            text: `${type === "url" ? "Link" : "Code"} copied and ready to be used`,
          },
        ]),
      )
      .catch(console.error);
  };

  const formActionItems: ContextMenuItemProps[] = [
    {
      label: form.active ? "Active" : "Inactive",
      toggled: form.active ?? false,
      disabled: toggleSlug.loading,
      hidden: !canActivate,
      keepOpen: true,
      onClick: () => {
        if (form.active && hasSubmissionLink) {
          setDeactivateModalOpen(true);
        } else {
          setActive(!form.active).catch(console.error);
        }
      },
    },
    {
      label: "Open form",
      leftIcon: "regularShare",
      onClick: () => navigate(`/embedded/builder/${extractId(form.uri)}`),
    },
    {
      label: "Duplicate form",
      leftIcon: "regularCopy",
      onClick: () => setDuplicatingForm(form),
      hidden: !canDuplicate,
    },
    {
      label: "Print",
      leftIcon: "regularPrint",
      onClick: () => setPrintingForm(form),
    },
    {
      label: "Delete form",
      leftIcon: "regularTrashCan",
      className: "text-red-550",
      hidden: !canDelete,
      onClick: () => setDeletingForm(form),
    },
    {
      label: "Move to global forms",
      leftIcon: "regularBooks",
      hidden: !globalForm || !canMoveToGlobal,
      onClick: () =>
        updateFormOwnership({
          variables: { uri: form.uri, teamUri: globalTeamUri },
        }),
    },
    {
      label: "Copy support information",
      onClick: () => {
        copySupportInformation({
          teamUri: user?.teamUri,
          formUri: form.uri,
          userUri: user?.userUri,
        });
      },
      internal: true,
    },
  ];

  const copyLinkSubMenu: DropdownPartProps[] =
    submissionLinkHashes?.map((hash) => ({
      label: hash.label?.trim() || form.formTitle,
      leftIcon: "regularLink",
      onClick: () => onCopyLink(hash, "url"),
    })) || [];

  const copyIframeSubMenu: DropdownPartProps[] =
    submissionLinkHashes?.map((hash) => ({
      label: hash.label?.trim() || form.formTitle,
      leftIcon: "regularLink",
      onClick: () => onCopyLink(hash, "iframe"),
    })) || [];

  const copyButtonSubMenu: DropdownPartProps[] =
    submissionLinkHashes?.map((hash) => ({
      label: hash.label?.trim() || form.formTitle,
      leftIcon: "regularLink",
      onClick: () => onCopyLink(hash, "button"),
    })) || [];

  const editSubmissionLinkSubMenu: DropdownPartProps[] =
    submissionLinkHashes?.map((hash) => ({
      label: hash.label?.trim() || form.formTitle,
      leftIcon: "regularLink",
      onClick: () => setEditingLinkHash(hash),
    })) || [];

  const deleteSubmissionLinkSubMenu: DropdownPartProps[] =
    submissionLinkHashes?.map((hash) => ({
      label: hash.label?.trim() || form.formTitle,
      leftIcon: "regularLink",
      onClick: () => setDeletingLinkHash(hash),
    })) || [];

  const viewQrCodeSubMenu: DropdownPartProps[] =
    submissionLinkHashes?.map((hash) => ({
      label: hash.label?.trim() || form.formTitle,
      leftIcon: "regularLink",
      onClick: () => {
        const link = toSubdomain(undefined, `/create/${extractId(hash.uri)}`);
        setGeneratingQRCode(link);
      },
    })) || [];

  const webEnquiryLinkActionItems: ContextMenuItemProps[] = [
    {
      label: "Copy Link",
      leftIcon: "regularCopy",
      subMenu: copyLinkSubMenu,
    },
    {
      label: "Copy Code: to Embed on Website",
      leftIcon: "regularCopy",
      subMenu: copyIframeSubMenu,
    },
    {
      label: 'Copy Code: "Contact Us" Button',
      leftIcon: "regularCopy",
      subMenu: copyButtonSubMenu,
    },
    {
      label: "View QR Code",
      leftIcon: "regularQrcode",
      subMenu: viewQrCodeSubMenu,
    },
    {
      label: "Edit",
      leftIcon: "regularPenToSquare",
      subMenu: editSubmissionLinkSubMenu,
    },
    {
      label: "Delete Link",
      leftIcon: "regularTrashCan",
      className: "text-red-550",
      subMenu: deleteSubmissionLinkSubMenu,
    },
  ];

  const menuShellProps = {
    button: {
      className: "w-[2rem] h-[2rem]",
      variant: "secondarySubtle" as const,
      leftIcon: "solidEllipsis" as const,
    },
    placement: "bottom-end" as const,
    offsetBy: 5,
    id: "form-action-menu",
    className: "w-fit",
  };

  return (
    <>
      {hasSubmissionLink && form.formCategory === "lead" ? (
        <ContextMenu
          {...menuShellProps}
          variant="grouped"
          items={[
            { heading: "Form Actions", items: formActionItems },
            {
              heading: `Web ${terms.enquiry.title} Link Actions`,
              items: webEnquiryLinkActionItems,
            },
          ]}
        />
      ) : (
        <ContextMenu {...menuShellProps} items={formActionItems} />
      )}
      <DeactivateFormModal
        open={deactivateModalOpen}
        loading={toggleSlug.loading}
        onClose={() => setDeactivateModalOpen(false)}
        onConfirm={async () => {
          await setActive(false);
          setDeactivateModalOpen(false);
        }}
      />
      {hasSubmissionLink && (
        <WebEnquiryLinkModal
          open={!!editingLinkHash}
          onClose={closeLinkModal}
          formTitle={form.formTitle || "Untitled Form"}
          submissionLinkHash={editingLinkHash}
          loading={savingLink}
          onSubmit={onSubmitWebEnquiryLink}
        />
      )}
      {hasSubmissionLink && (
        <DeleteWebEnquiryLinkModal
          open={!!deletingLinkHash}
          loading={deletingLink}
          onClose={closeDeleteLinkModal}
          onConfirm={confirmDeleteLink}
        />
      )}
      <GenerateQRCode
        link={generatingQRCode}
        onClose={() => setGeneratingQRCode(undefined)}
        onCopied={() =>
          setToasts([{ text: "QR code copied", width: "medium" }])
        }
        onDownloaded={() =>
          setToasts([{ text: "QR code downloaded", width: "medium" }])
        }
      />
    </>
  );
};
